import React, { useMemo, useState } from "react";
import { api, agencyApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, FileSpreadsheet } from "lucide-react";
import { SourceStep, Preview } from "@/components/sheets/ImportSheetDialog";
import { colLetter } from "@/lib/sheetFormula";

const L = ({ children }) => <label className="text-[10px] text-[#9FA1A4] mb-1 block font-mono-data uppercase tracking-wide">{children}</label>;
const SEL = "h-8 rounded-md bg-[#F6F6FD] border border-[#EEEFEF] px-2 text-xs w-full";
const TYPES = ["text", "email", "phone", "number", "date", "url"];
const NEW = "__new", SKIP = "__skip", NEW_LIST = "__newlist";
const all = (vals, re) => { const f = vals.filter((v) => v.trim()); return f.length > 0 && f.every((v) => re.test(v.trim())); };
const infer = (label, vals) => {
  const l = (label || "").toLowerCase();
  if (all(vals, /^[^\s@]+@[^\s@]+\.[^\s@]+$/) || l.includes("email")) return "email";
  if (l.includes("phone") || l.includes("mobile") || all(vals, /^\+?[\d\s().-]{6,}$/) && !all(vals, /^-?\d+(\.\d+)?$/)) return "phone";
  if (all(vals, /^https?:\/\//)) return "url";
  if (all(vals, /^(\d{4}-\d{2}-\d{2}|\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4})/) || l === "date") return "date";
  if (all(vals, /^-?[\d,]*\.?\d+$/)) return "number";
  return "text";
};
const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

function ColumnRow({ i, label, sample, m, fields, onChange }) {
  return (
    <div className="grid grid-cols-[1fr_1fr_110px] gap-2 items-center" data-testid={`lead-import-col-${i}`}>
      <div className="min-w-0"><p className="text-xs font-medium text-[#06060C] truncate">{label || `Column ${colLetter(i)}`}</p><p className="text-[10px] text-[#9FA1A4] truncate">{sample || "—"}</p></div>
      <select data-testid={`lead-import-target-${i}`} className={SEL} value={m.target} onChange={(e) => onChange({ ...m, target: e.target.value })}>
        <option value={NEW}>New field</option>
        {fields.map((f) => <option key={f.key} value={f.key}>Into: {f.label}</option>)}
        <option value={SKIP}>Don't import</option>
      </select>
      <select data-testid={`lead-import-type-${i}`} disabled={m.target !== NEW} className={`${SEL} disabled:opacity-40`} value={m.type} onChange={(e) => onChange({ ...m, type: e.target.value })}>
        {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
    </div>
  );
}

export function LeadImportDialog({ lists, activeId, onClose, onDone }) {
  const [parsed, setParsed] = useState(null);
  const [tab, setTab] = useState(0);
  const [header, setHeader] = useState(true);
  const [dest, setDest] = useState(activeId || NEW_LIST);
  const [listName, setListName] = useState("");
  const [maps, setMaps] = useState({});
  const [dateIdx, setDateIdx] = useState("");
  const [busy, setBusy] = useState(false);

  const rows = parsed?.tabs?.[tab]?.rows || [];
  const width = rows[0]?.length || 0;
  const headers = header ? rows[0] || [] : Array.from({ length: width }, (_, i) => `Column ${colLetter(i)}`);
  const data = header ? rows.slice(1) : rows;
  const fields = useMemo(() => (dest === NEW_LIST ? [] : lists.find((l) => l.id === dest)?.fields || []), [dest, lists]);

  const defaultMap = (i) => {
    const hit = fields.find((f) => norm(f.label) === norm(headers[i]));
    return hit ? { target: hit.key, type: hit.type } : { target: NEW, type: infer(headers[i], data.slice(0, 50).map((r) => r[i] || "")) };
  };
  const mapFor = (i) => maps[`${dest}|${tab}|${header}|${i}`] || defaultMap(i);
  const setMap = (i, m) => setMaps((p) => ({ ...p, [`${dest}|${tab}|${header}|${i}`]: m }));

  const onParsed = (d) => {
    setParsed(d); setTab(0); setListName(d.name || "");
    const auto = (d.tabs[0]?.rows?.[0] || []).findIndex((h) => /^(date|created|submitted|timestamp)/i.test(h || ""));
    setDateIdx(auto >= 0 ? String(auto) : "");
  };

  const submit = async () => {
    setBusy(true);
    try {
      const columns = Array.from({ length: width }, (_, i) => ({ index: i, label: headers[i] || `Column ${colLetter(i)}`, ...mapFor(i) }));
      const body = { columns, rows: data, date_index: dateIdx === "" ? null : Number(dateIdx), ...(dest === NEW_LIST ? { new_list_name: listName } : { list_id: dest }) };
      const { data: res } = await api.post("/leads/import", body);
      toast.success(`Imported ${res.imported} lead${res.imported === 1 ? "" : "s"}${res.new_fields ? ` · ${res.new_fields} new field${res.new_fields === 1 ? "" : "s"}` : ""}`);
      onDone(res.list_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-[#EEEFEF] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lead-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-[#723FFF]" /> Import leads</DialogTitle>
          <DialogDescription className="text-[#9FA1A4]">Bring in an existing Google Sheet, CSV or Excel file. Columns become lead fields and each row becomes a lead.</DialogDescription>
        </DialogHeader>
        {!parsed ? <SourceStep onParsed={onParsed} client={agencyApi} /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {parsed.tabs.length > 1 && <div><L>Tab</L><select data-testid="lead-import-tab" className={SEL} value={tab} onChange={(e) => { setTab(Number(e.target.value)); setDateIdx(""); }}>{parsed.tabs.map((t, i) => <option key={i} value={i}>{t.name} ({t.rows.length} rows)</option>)}</select></div>}
              <div><L>Import into</L><select data-testid="lead-import-dest" className={SEL} value={dest} onChange={(e) => setDest(e.target.value)}>
                <option value={NEW_LIST}>A new list</option>{lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select></div>
              {dest === NEW_LIST && <div><L>New list name</L><Input data-testid="lead-import-list-name" value={listName} onChange={(e) => setListName(e.target.value)} className="h-8 bg-[#F6F6FD] border-[#EEEFEF] text-xs" /></div>}
              <div><L>Lead date column</L><select data-testid="lead-import-date-col" className={SEL} value={dateIdx} onChange={(e) => setDateIdx(e.target.value)}>
                <option value="">None — use today</option>{headers.map((h, i) => <option key={i} value={i}>{h || `Column ${colLetter(i)}`}</option>)}
              </select></div>
            </div>
            <label className="flex items-center gap-2 text-xs text-[#595D62]"><input type="checkbox" data-testid="lead-import-header" checked={header} onChange={(e) => { setHeader(e.target.checked); if (!e.target.checked) setDateIdx(""); }} /> First row is column names</label>
            <Preview headers={headers} data={data} />
            <div><L>Columns</L><div className="space-y-2 rounded-xl border border-[#EEEFEF] p-3 max-h-72 overflow-y-auto">
              {headers.map((h, i) => <ColumnRow key={`${dest}-${tab}-${i}`} i={i} label={h} sample={data.find((r) => r[i])?.[i]} m={mapFor(i)} fields={fields} onChange={(m) => setMap(i, m)} />)}
            </div></div>
            <p className="text-[11px] text-[#9FA1A4]" data-testid="lead-import-summary">{data.length} lead{data.length === 1 ? "" : "s"} will be imported as "{(lists.find((l) => l.id === dest)?.statuses || ["New"])[0]}". Matching emails or phones are flagged as duplicates for review.</p>
          </div>
        )}
        <DialogFooter>
          {parsed && <Button variant="outline" data-testid="lead-import-back" onClick={() => setParsed(null)} className="rounded-full border-[#EEEFEF]">Back</Button>}
          {parsed && <Button data-testid="lead-import-submit" disabled={busy || !data.length} onClick={submit} className="rounded-full bg-[#06060C] text-white hover:bg-[#1c1c24]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import ${data.length} leads`}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
